import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/i18n";

const TILES = [
  ["appointments_today", "Today's Confirmed Sessions", "/staff/calendar"],
  ["appointment_requests", "Appointment Requests To Confirm", "/staff/calendar?tab=requests"],
  ["eligibility_to_verify", "Eligibility To Verify", "/staff/intake"],
  ["cases_to_match", "Cases To Match", "/staff/cases?state=matching"],
  ["certificates_issued", "Certificates Issued", "/staff/cases?state=certificate_issued"],
  ["placeholder_videos", "Placeholder Videos Needing Links", "/staff/videos"],
  ["next_steps_reviews", "Next-Steps Reviews", "/staff/calendar?tab=reviews"],
  ["waitlist_waiting", "People On The Waitlist", "/staff/calendar?tab=waitlist"],
  ["messages_awaiting", "Portal Messages Awaiting Reply", "/staff/messages"],
  ["reach_out_open", "Reach Out Awaiting Response", "/staff/reach-out"],
  ["classes_this_week", "Classes This Week", "/staff/classes"],
];

export default function StaffDashboard() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["staff", "dashboard"], queryFn: async () => (await api.get("/staff/dashboard")).data, refetchInterval: 60_000 });
  const isAdmin = user.roles.includes("system_admin") || user.roles.includes("care_director");
  const { data: placeholders } = useQuery({ queryKey: ["admin", "placeholders"], queryFn: async () => (await api.get("/admin/placeholders")).data, enabled: isAdmin });
  const urgent = data?.urgent_items || [];

  return (
    <div data-testid="staff-dashboard">
      <h1 className="text-4xl">{t("staff.dashboard")}</h1>

      <section className={`mt-8 rounded-md border p-6 ${urgent.length ? "border-2 border-terracotta-600" : "border-sage-400/60 bg-cream-100"}`} aria-labelledby="urgent-heading" data-testid="urgent-section">
        <h2 id="urgent-heading" className={`text-2xl ${urgent.length ? "text-terracotta-600" : ""}`}>{t("staff.urgentHeading")}</h2>
        {urgent.length === 0 ? <p className="mt-2 text-ink-500" data-testid="urgent-empty">{t("staff.noUrgent")}</p> : (
          <ul className="mt-3 divide-y divide-terracotta-600/30" data-testid="urgent-dashboard-list">
            {urgent.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <span><span className="font-semibold">Case {u.case_code || "—"}</span> <span className="text-ink-500">· {u.source.replace("_", " ")} · {new Date(u.created_at).toLocaleString("en-US", { timeZone: "America/Los_Angeles", dateStyle: "medium", timeStyle: "short" })}{u.acknowledged_at ? " · acknowledged" : " · not acknowledged"}{u.realert_count ? ` · re-alerted ${u.realert_count}×` : ""}</span></span>
                <Link to="/staff/urgent" className="btn-status min-h-[40px]" data-testid={`dashboard-urgent-${u.id}`}>Respond</Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10" aria-label="Today at a glance">
        <dl className="grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
          {TILES.map(([key, label, to]) => (
            <div key={key} className="border-t border-sage-400/60 pt-4" data-testid={`tile-${key}`}>
              <dt className="text-ink-500">{label}</dt>
              <dd className="mt-1 flex items-baseline gap-3"><span className="font-serif text-4xl font-semibold">{data?.counts?.[key] ?? "—"}</span><Link to={to} className="link text-sm">Open</Link></dd>
            </div>
          ))}
          <div className="border-t border-sage-400/60 pt-4" data-testid="tile-people">
            <dt className="text-ink-500">Active People</dt>
            <dd className="mt-1 flex items-baseline gap-3"><span className="font-serif text-4xl font-semibold">{data?.counts?.people ?? "—"}</span>{isAdmin && <Link to="/staff/people" className="link text-sm">Open</Link>}</dd>
          </div>
          <div className="border-t border-sage-400/60 pt-4" data-testid="tile-outbox">
            <dt className="text-ink-500">Messages Sent</dt>
            <dd className="mt-1 flex items-baseline gap-3"><span className="font-serif text-4xl font-semibold">{data?.counts?.outbox ?? "—"}</span><Link to="/staff/communications" className="link text-sm">Open</Link></dd>
          </div>
        </dl>
      </section>

      <div className="mt-14 grid gap-12 lg:grid-cols-2">
        <section aria-labelledby="phases-heading">
          <h2 id="phases-heading" className="text-2xl">Build Phases</h2>
          <ol className="mt-4 divide-y divide-sage-400/50 border-y border-sage-400/50">
            {(data?.phases || []).map((p) => (
              <li key={p.phase} className="flex items-center justify-between gap-4 py-3" data-testid={`phase-${p.phase}`}>
                <span><span className="text-ink-500 mr-2">{p.phase}.</span>{p.title}</span>
                <span className="text-sm text-ink-500">{p.status.replace("_", " ")}</span>
              </li>
            ))}
          </ol>
        </section>
        {isAdmin && placeholders && (
          <section aria-labelledby="placeholders-heading" data-testid="placeholders-section">
            <h2 id="placeholders-heading" className="text-2xl">Remaining Placeholders</h2>
            <p className="mt-2 text-ink-500">Values the spec left bracketed. Set them in Settings or Content.</p>
            <ul className="mt-4 divide-y divide-sage-400/50 border-y border-sage-400/50">
              {placeholders.settings.map((s) => <li key={s.key} className="py-2.5 flex justify-between gap-4"><span>{s.label}</span><Link to="/staff/settings" className="link text-sm">Set</Link></li>)}
              {placeholders.county_crisis_lines_unverified.map((c) => <li key={c} className="py-2.5 flex justify-between gap-4"><span>{c} County crisis line</span><Link to="/staff/resources" className="link text-sm">Verify</Link></li>)}
              {placeholders.content_needing_review.map((b) => <li key={b.key} className="py-2.5 flex justify-between gap-4"><span>{b.title || b.key} (needs review)</span><Link to="/staff/content" className="link text-sm">Review</Link></li>)}
              <li className="py-2.5">{placeholders.placeholder_areas_of_need} placeholder areas of need (Care Director to supply the real list)</li>
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
